/**
 * Utility functions for Chatty component
 */

/**
 * Sanitizes AI response to maintain consistent identity
 * @param {string} response - The raw AI response text
 * @returns {string} - Sanitized response
 */
export function sanitizeAIResponse(response) {
    const fallback = "I am Chatty, a helpful AI assistant created to assist with various tasks!";

    // Only match first-person identity claims: "I am GPT", "I'm created by OpenAI", etc.
    const selfIdentityPatterns = [
        /\bi\s*'?\s*m\s+(gpt|chatgpt|openai|claude|anthropic|gemini|google\s*ai|bard)/i,
        /\bi\s+am\s+(gpt|chatgpt|openai|claude|anthropic|gemini|google\s*ai|bard|an?\s+ai\s+(by|from)\s+(openai|anthropic|google))/i,
        /\bi\s+(was|am|'m)\s+(created|developed|made|built)\s+by\s+(openai|anthropic|google|meta)/i,
        /\bmy\s+(name\s+is|creator\s+is|developer\s+is)\s+(gpt|chatgpt|openai|anthropic)/i
    ];

    // Replace matching sentences with fallback, not entire response
    let sanitized = response;
    for (const pattern of selfIdentityPatterns) {
        sanitized = sanitized.replace(pattern, fallback);
    }

    return sanitized;
}

/**
 * Updates user stats from API response headers
 * @param {Response} response - Fetch API response
 * @param {Function} setUser - User state updater function
 */
export const updateUserStatsFromHeaders = (response, setUser) => {
  let foundHeaders = {};
  for (let [key, value] of response.headers.entries()) {
    foundHeaders[key.toLowerCase()] = value;
  }

  const messagesUsed = foundHeaders["x-messages-used"] || response.headers.get("X-Messages-Used");
  const limit = foundHeaders["x-messages-limit"] || response.headers.get("X-Messages-Limit");
  const resetDate = foundHeaders["x-reset-date"] || response.headers.get("X-Reset-Date");

  if (!messagesUsed || !limit || !resetDate) {
    console.warn("⚠️ Missing headers in API response! Check fetch() handling.");
  }

  if (messagesUsed && limit && resetDate) {
    setUser((prevUser) => ({
      ...prevUser,
      'custom:messagesUsed': messagesUsed,
      'custom:limit': limit,
      'custom:resetDate': resetDate,
    }));
  }
};

/**
 * Logs cost calculation for AI model usage (development mode only)
 * @param {string} model - AI model name
 * @param {number} inputTokens - Number of input tokens
 * @param {number} outputTokens - Number of output tokens
 */
export const logCostCalculation = (model, inputTokens, outputTokens) => {
  const COST_PER_MODEL = {
    "gpt-4o": { input: 2.50, output: 10 },
    "o3-mini": { input: 1.10, output: 4.40 },
    "grok-2-vision-1212": { input: 2, output: 10 }
  };
  
  const devLog = (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(...args);
    }
  };
  
  const devWarn = (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(...args);
    }
  };
  
  if (model === "deepseek-r1:32b") {
    devLog(`Model "${model}" runs locally and has no cost.`);
    devLog(`Input Tokens: ${inputTokens}, Output Tokens: ${outputTokens}`);
  } else {
    const pricing = COST_PER_MODEL[model];
    if (!pricing) {
      devWarn(`⚠️ Warning: No pricing found for model "${model}". Defaulting to $0.`);
      return;
    }
    
    const calculateCost = (tokens, rate) => (tokens / 1_000_000) * rate;
    const inputCost = calculateCost(inputTokens, pricing.input || 0);
    const outputCost = calculateCost(outputTokens, pricing.output || 0);
    const totalCost = inputCost + outputCost;
    
    devLog(`Model: ${model}`);
    devLog(`Input Tokens: ${inputTokens}, Cost: $${inputCost.toFixed(4)}`);
    devLog(`Output Tokens: ${outputTokens}, Cost: $${outputCost.toFixed(4)}`);
    devLog(`Total Cost: $${totalCost.toFixed(4)}`);
  }
};

/**
 * Saves messages to local storage or cloud
 * @param {Array} messages - Array of chat messages
 * @param {boolean} useCloudStorage - Whether to use cloud storage
 */
export const saveMessages = (messages, useCloudStorage = false) => {
  if (!useCloudStorage) {
    localStorage.setItem("chattyMessages", JSON.stringify(messages));
  } else {
    fetch("https://your-api.com/storeChat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages }),
    });
  }
};